import React, { memo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store'
import { getSecurityAsync, getSecurityKey } from '../../../../util/security'
import { Box, Typography } from '@mui/material'
import QuickWidgetDialog from '../../../quickWidgetDialog/quickWidgetDialog'
import { QuickWidgetTypes } from '../../../../reducer/user.reducer'
import { toLocaleStringIn } from '../../../../util/general'
import { makeSelectQuickModeEnabled } from '../../../../selectors/user'
import { selectPositions } from '../../../../selectors/positions'
import PositionsTable from '../../../positionsTable/positionsTable'
import { selectSnapshots } from '../../../../selectors/security'

const PositionsWidget: React.FC = () => {
    const positions = useSelector(selectPositions);
    const snapshots = useSelector(selectSnapshots)
    const QuickEnableMode= useSelector(makeSelectQuickModeEnabled(QuickWidgetTypes.POSITION))

    const loading = useSelector((state: RootState) => state.positionsStore.loading);
    const [totalPnL, setTotalPnL] = useState(0)

    useEffect(() => {
        if (!positions.length) {
            setTotalPnL(0)
            return
        }
        let cancelled = false
        const run = async () => {
            const pnl = await positions.reduce(async (sumPromise, position) => {
                const sum = await sumPromise
                const securityKey = getSecurityKey(position.security.exchange, position.security.code)
                const security = await getSecurityAsync(securityKey)
                const snapshot = snapshots[securityKey]
                const ltp = snapshot?.lastTradedPrice || 0
                const qty = position.closePosition
                const pm = security?.priceMultiplier ?? 100
                if (qty !== 0) {
                    const avgPrice = position.closePositionValue / qty
                    const pnlItem = (ltp - avgPrice) * qty
                    return sum + pnlItem / pm
                }
                return sum + (position.realisedProfit ?? 0) / pm
            }, Promise.resolve(0))
            if (!cancelled) setTotalPnL(pnl)
        }
        run()
        return () => { cancelled = true }
    }, [positions, snapshots])

    const title = (
        <Box display="flex" alignItems="center">
            <Typography fontSize={14}>Open Positions</Typography>
            <Typography fontSize={14} mx={1}> • </Typography>
            <Typography fontSize={12} fontWeight="light" mr={0.5}>P/L</Typography>
            <Typography fontSize={14} >
                <span className={totalPnL < 0 ? 'red-text' : 'green-text'}>{toLocaleStringIn(totalPnL)}</span>
            </Typography>
        </Box>
    );
    
    return (
        <QuickWidgetDialog widgetType={QuickWidgetTypes.POSITION} title={title}>
            {
            !QuickEnableMode && loading ? (
                <Box p={4} textAlign="center">Loading...</Box>
            ) : positions.length === 0 ? (
                <Box p={4} textAlign="center">No open positions</Box>
            ) : (
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <PositionsTable positions={positions} />
                </Box>
            )}
        </QuickWidgetDialog>
    );
};

export default memo(PositionsWidget);